<template>
  <div>
    <div class="distributions-header">
      <div class="section-header">
        <h2>CALCULATION BREAKDOWN</h2>
      </div>
    </div>
    <!-- EOS distributions breakdown -->
    <template v-if="plan.isEos">
      <table id="distributions-table">
        <thead>
          <tr>
            <th />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <th
                v-if="(index) === payoutCount"
                class="final-distribution"
                style="width: 12rem;"
              >
                Final
              </th>
              <th
                v-else
                :key="index"
                class="forecasted-distribution"
                style="width: 12rem;"
              />
            </template>
          </tr>
          <tr>
            <th />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <th
                v-if="payoutCount === 4"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                Q{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
              <th
                v-else-if="payoutCount === 3"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                T{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
              <th
                v-else-if="payoutCount === 2"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                P{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-performance-level'">
                Performance Level
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td :class="[index === distributionCount ? 'performance-level' : '']">
                <template v-if="distributions[index - 1]">
                  {{ distributions[index - 1].company_performance_position }}
                </template>
              </td>
            </template>
          </tr>
          <tr v-if="distributionCount == payoutCount">
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-final-annual-award-value'">
                Final Annual Award Value
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="index == payoutCount && distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].ytd_bonus) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-annual-award-value'">
                Potential Annual Award Value
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="distributions[index - 1]">
                  <!-- Old statements may not have the potential award value, so fall back to the forecast value -->
                  {{ formatCurrency(distributions[index - 1].potential_annual_award ?? distributions[index - 1].forecasted_annual_award) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column pt-4">
              <QuestionBadge :id="'pendo-eos-period-percent'">
                Period Allocation %
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <div v-if="distributions[index - 1]" class="distribution-symbol pt-4">
                  <span style="text-align: center;">X</span>
                  {{ formatPercent(10000000 / (index != payoutCount ? payoutCount : 1), 2) }}
                </div>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-potential-quarterly-payment'">
                <template v-if="payoutCount === 4">
                  Potential Quarterly Payment (PQP)
                </template>
                <template v-else-if="payoutCount === 3">
                  Potential Trimester Payment (PTP)
                </template>
                <template v-else-if="payoutCount === 2">
                  Potential Semi-annual Payment (PSAP)
                </template>
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="distributions[index - 1]">
                  <!-- Old statements may not have the potential award value, so fall back to the forecast value -->
                  <div class="font-bold border-t" style="font-size: 1.3333rem">{{ formatCurrency(distributions[index - 1].potential_payout ?? distributions[index - 1].prorated_forecast) }}</div>
                </template>
              </td>
            </template>
          </tr>
          <template v-if="hasOrgUnits">
            <tr>
              <td class="first-column pt-8">
                <QuestionBadge :id="'pendo-eos-allocation-percent'">
                  {{ client.allocation.label }} Allocation %
                </QuestionBadge>
              </td>
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div v-if="distributions[index - 1]" class="distribution-symbol indent pt-8">
                    <span>X</span>
                    <template v-if="payoutCount === 4">
                      PQP
                    </template>
                    <template v-else-if="payoutCount === 3">
                      PTP
                    </template>
                    <template v-else-if="payoutCount === 2">
                      PSAP
                    </template>
                    <br>
                    {{ formatPercent(distributions[index - 1].corp_weight, 2) }}
                  </div>
                </td>
              </template>
            </tr>
            <tr>
              <td class="first-column">
                <!-- <QuestionBadge :id="'pendo-eos-client-allocation-label'"> -->
                  <!-- {{ client.allocation.label }} -->
                <!-- </QuestionBadge> -->
              </td>
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div v-if="distributions[index - 1]" class="font-bold border-t highlight distribution-symbol">
                    <span style="font-size: 20px" class="highlight">+</span>
                    {{ formatCurrency(index == payoutCount ? client.allocation.contribution : distributions[index - 1].corp_contribution) }}
                  </div>
                </td>
              </template>
            </tr>
            <template
              v-for="(unit, index) in Object.values(organization.units)"
              :key="`${unit[unit.length - 1].label}-weight-${index}`"
            >
              <tr>
                <td class="first-column pt-8">
                  <QuestionBadge :id="'pendo-eos-org-unit-name'">
                    {{ organization.types[unit[unit.length - 1].type_id].label }} Allocation and Modification %
                  </QuestionBadge>
                </td>
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="unit[index - 1]"
                      class="distribution-symbol indent pt-8"
                    >
                      <span>X</span>
                      <template v-if="payoutCount === 4">
                        PQP
                      </template>
                      <template v-else-if="payoutCount === 3">
                        PTP
                      </template>
                      <template v-else-if="payoutCount === 2">
                        PSAP
                      </template>
                      <br>
                      {{ formatPercent(unit[index - 1].allocation.weight, 2) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="unit[index - 1]"
                      class="border-t ml-3rem "
                    >
                      {{ formatCurrency(unit[index - 1].allocation.funding) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="unit[index - 1]"
                      class="distribution-symbol indent"
                    >
                      <span>X</span> {{ formatPercent(unit[index - 1].allocation.modifier) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="unit[index - 1]"
                      class="font-bold border-t highlight distribution-symbol"
                    >
                      <span style="font-size: 20px" class="highlight">+</span>
                      {{ formatCurrency(unit[index - 1].allocation.contribution) }}
                    </div>
                  </td>
                </template>
              </tr>
            </template>
            <tr>
              <td class="first-column pt-8" style="vertical-align: bottom;" />
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td style="vertical-align: top;" class="pt-8">
                  <template v-if="distributions[index - 1]">
                    <div class="font-bold border-t pt-4 highlight distribution-symbol">
                      <span style="font-size: 20px" class="highlight">=</span>
                      {{ formatCurrency(distributions[index - 1].prorated_forecast) }}
                    </div>
                  </template>
                </td>
              </template>
            </tr>
          </template>
          <template v-if="employeeGoals || employeeRating">
            <tr>
              <td class="first-column">
                <QuestionBadge :id="'pendo-eos-individual-performance'">
                  Individual Performance Adjustment
                </QuestionBadge>
              </td>
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="distributions[index - 1]"
                    class="distribution-symbol indent"
                  >
                    <span>X</span>{{ formatPercent(distributions[index - 1].ind_mult, 2) }}
                  </div>
                </td>
              </template>
            </tr>
            <tr>
              <td class="first-column" />
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="distributions[index - 1]"
                    class="font-bold border-t highlight"
                  >
                    {{ formatCurrency(distributions[index - 1].raw_payout) }}
                  </div>
                </td>
              </template>
            </tr>
          </template>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-holdback-percent'">
                Holdback <span style="margin-left:0.5rem;">{{ formatPercent(plan.holdback.percent) }}</span>
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="index !== payoutCount && distributions[index - 1]">
                  <div class="distribution-symbol indent">
                    <span style="font-size: 20px;">-</span>{{ formatCurrency(distributions[index - 1].holdback_amount) }}
                  </div>
                </template>
              </td>
            </template>
          </tr>
          <tr v-if="distributionCount == payoutCount">
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-less-previously-paid'">
                Less Previously Paid
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="index === payoutCount && distributions[index - 1]">
                  <div class="distribution-symbol indent">
                    <span style="font-size: 20px;">-</span>{{ formatCurrency(distributions[index - 1].sum_of_previous) }}
                  </div>
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column" />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td class="font-bold">
                <template v-if="distributions[index - 1]">
                  <div class="font-bold border-t highlight distribution-symbol">
                    <span style="font-size: 20px" class="highlight">=</span>
                    {{ formatCurrency(distributions[index - 1].eligible) }}
                  </div>
                </template>
              </td>
            </template>
          </tr>
          <tr v-if="distributions.some(({adjustments}) => adjustments != 0)">
            <td
              class="first-column"
              style="padding-bottom:1em;"
            >
              <QuestionBadge :id="'pendo-eos-employer-adjustment'">
                Employer Adjustment
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td style="padding-bottom:1em;">
                <template v-if="distributions[index - 1] && distributions[index - 1].adjustments !== 0">
                  {{ distributions[index - 1].adjustments < 0 ? '-' : '+' }}{{ formatCurrency(distributions[index - 1].adjustments) }}
                </template>
              </td>
            </template>
          </tr>
          <!-- <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-eos-annual-award-value'">
                Forecasted Annual Award Value
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="index != payoutCount && distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].forecasted_annual_award) }}
                </template>
              </td>
            </template>
          </tr> -->
        </tbody>
        <tfoot>
          <tr>
            <td
              class="first-column distribution-footer"
              style="vertical-align:bottom;"
            >
              <QuestionBadge :id="'pendo-period-payment'">
                <template v-if="payoutCount === 4">
                  Quarterly Payment
                </template>
                <template v-else-if="payoutCount === 3">
                  Trimester Payment
                </template>
                <template v-else-if="payoutCount === 2">
                  Semi-annual Payment
                </template>
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td
                class="distribution-amount"
                style="vertical-align:bottom;"
              >
                <template v-if="distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].award_amount) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td
              class="first-column distribution-footer"
              style="vertical-align:top;"
            >
              <QuestionBadge :id="'pendo-paid-to-date'">
                Paid to Date
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td
                class="ytd-bonus"
                style="vertical-align:top;"
              >
                <template v-if="distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].ytd_bonus) }}
                </template>
              </td>
            </template>
          </tr>
        </tfoot>
      </table>
    </template>

    <!-- standard distributions breakdown -->
    <template v-else>
      <table id="distributions-table">
        <thead>
          <tr>
            <th />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <th
                v-if="index === payoutCount"
                class="final-distribution"
                style="width: 12rem;"
              >
                Final
              </th>
              <th
                v-else
                :key="index"
                class="forecasted-distribution"
                style="width: 12rem;"
              >
                Forecasted
              </th>
            </template>
          </tr>
          <tr>
            <th />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <th
                v-if="payoutCount === 4"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                Q{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
              <th
                v-else-if="payoutCount === 3"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                T{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
              <th
                v-else-if="payoutCount === 2"
                class="distribution-period"
              >
                <span
                  class="prev-period"
                  :class="{disabled:selectedPeriod == 1}"
                  @click="selectPrevPeriod"
                >&lt;</span>
                SA{{ index }}
                <span
                  class="next-period"
                  :class="{disabled:selectedPeriod == payoutCount}"
                  @click="selectNextPeriod"
                >&gt;</span>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              class="first-column"
              style="padding-top:2rem;"
            >
              <QuestionBadge :id="'pendo-performance-level'">
                Performance Level
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td
                :class="[index === distributionCount ? 'performance-level' : '']"
                style="padding-top:2rem;"
              >
                <template v-if="distributions[index - 1]">
                  {{ distributions[index - 1].company_performance_position }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-annual-award-value'">
                Potential Annual Award (PAA)
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].potential_annual_award ?? distributions[index - 1].forecasted_annual_award) }}
                </template>
              </td>
            </template>
          </tr>
          <template v-if="hasOrgUnits || employee.performance.mode == 'as-component'">
            <tr>
              <td class="first-column pt-4">
                <QuestionBadge :id="'pendo-allocation-percent'">
                  {{ client.allocation.label }} Allocation %
                </QuestionBadge>
              </td>
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="index === payoutCount && distributions[index - 1]"
                    class="distribution-symbol pt-4 indent"
                  >
                    <span>X</span>PAA<br>{{ formatPercent(distributions[index - 1].corp_weight, 2) }}
                  </div>
                </td>
              </template>
            </tr>
            <tr>
              <td class="first-column" />
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="index === payoutCount && distributions[index - 1]"
                    class="font-bold"
                    :class="[distributions[index - 1]? 'border-t distribution-symbol highlight' : '']"
                  >
                    <span class="highlight">+</span>{{ formatCurrency(client.allocation.contribution) }}
                  </div>
                </td>
              </template>
            </tr>
            <template
              v-for="(unit, unit_index) in Object.values(organization.units)"
              :key="`${unit.label}-weight-${unit_index}`"
            >
              <tr>
                <td class="first-column pt-8">
                  <QuestionBadge :id="'pendo-org-unit-name'">
                    {{ organization.types[unit.type_id].label }} Allocation and Modification %
                  </QuestionBadge>
                </td>
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="distribution-symbol pt-8 indent"
                    >
                      <span>X</span>PAA<br>{{ formatPercent(unit.allocation.weight, 2) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold"
                      :class="[distributions[index - 1]? 'border-t' : '']"
                    >
                      {{ formatCurrency(unit.allocation.funding) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="distribution-symbol indent"
                    >
                      <span>X</span> {{ formatPercent(unit.allocation.modifier, 2) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold border-t distribution-symbol highlight"
                    >
                      <span class="highlight">+</span>{{ formatCurrency(unit.allocation.contribution) }}
                    </div>
                  </td>
                </template>
              </tr>
            </template>
            <tr v-if="employee.performance.mode !== 'as-component'">
              <td class="first-column" />
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="distributions[index - 1] && index === payoutCount"
                    class="font-bold"
                    :class="[distributions[index - 1]? 'border-t highlight distribution-symbol' : '']"
                  >
                    <span class="highlight">=</span>{{ formatCurrency(employee.allocation.contribution + client.allocation.contribution + Object.values(organization.units).reduce((prev, value) => prev + value.allocation.contribution, 0)) }}
                  </div>
                </td>
              </template>
            </tr>
          </template>
          <template v-if="employeeGoals || employeeRating">
            <template v-if="employee.performance.mode == 'as-component'">
              <tr>
                <td class="first-column pt-8">Individual Allocation %</td>
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="distribution-symbol pt-8 indent"
                    >
                      <span>X</span>PAA<br>{{ formatPercent(employee.allocation.weight, 2) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold border-t"
                    >
                      {{ formatCurrency(employee.allocation.funding) }}
                    </div>
                  </td>
                </template>
              </tr>
            </template>
            <tr>
              <td class="first-column">
                <QuestionBadge :id="'pendo-individual-performance'">
                  Individual Performance Adjustment
                </QuestionBadge>
              </td>
              <template
                v-for="index in payoutCount"
                :key="index"
              >
                <td>
                  <div
                    v-if="distributions[index - 1] && index === payoutCount && employeeGoals"
                    class="distribution-symbol indent"
                  >
                    <span>X</span>{{ formatPercent(employee.performance.goals.performance_total) }}
                  </div>
                  <div
                    v-else-if="distributions[index - 1] && index === payoutCount && employeeRating"
                    class="distribution-symbol indent"
                  >
                    <span>X</span>{{ formatPercent(employee.performance.rating.achieved_value) }}
                  </div>
                </td>
              </template>
            </tr>
            <template v-if="employee.performance.mode == 'as-component'">
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold"
                      :class="[distributions[index - 1]? 'border-t highlight distribution-symbol' : '']"
                    >
                      <span class="highlight">+</span>{{ formatCurrency(employee.allocation.contribution) }}
                    </div>
                  </td>
                </template>
              </tr>
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold"
                      :class="[distributions[index - 1]? 'border-t highlight distribution-symbol' : '']"
                    >
                      <span class="highlight">=</span>{{ formatCurrency(employee.allocation.contribution + client.allocation.contribution + Object.values(organization.units).reduce((prev, value) => prev + value.allocation.contribution, 0)) }}
                    </div>
                  </td>
                </template>
              </tr>
            </template>
            <template v-if="employee.performance.mode == 'as-modifier'">
              <tr>
                <td class="first-column" />
                <template
                  v-for="index in payoutCount"
                  :key="index"
                >
                  <td>
                    <div
                      v-if="distributions[index - 1] && index === payoutCount"
                      class="font-bold"
                      :class="[distributions[index - 1]? 'border-t highlight distribution-symbol' : '']"
                    >
                      <span class="highlight">=</span>{{ formatCurrency(distributions[index - 1].raw_payout) }}
                    </div>
                  </td>
                </template>
              </tr>
            </template>
          </template>
          <tr>
            <td class="first-column pt-4">
              <QuestionBadge :id="'pendo-percent-of-year-complete'">
                % of Year Completed
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <div class="distribution-symbol pt-4">
                  <template v-if="distributions[index - 1]">
                    <span style="text-align: center;">X</span>{{ formatPercent(distributions[index - 1].percent_complete) }}
                  </template>
                </div>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column" />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <div
                  class="font-bold"
                  :class="[distributions[index - 1]? 'border-t' : '']"
                >
                  <template v-if="distributions[index - 1]">
                    {{ formatCurrency(distributions[index - 1].raw_payout) }}
                  </template>
                </div>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column">
              Holdback <span style="margin-left:0.5rem;">{{ formatPercent(plan.holdback.percent) }}</span>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="distributions[index - 1] && index !== payoutCount">
                  -{{ formatCurrency(distributions[index - 1].holdback_amount) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column" />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <div
                  class="font-bold"
                  :class="[distributions[index - 1]? 'border-t' : '']"
                >
                  <template v-if="distributions[index - 1]">
                    {{ formatCurrency(distributions[index - 1].eligible_to_date) }}
                  </template>
                </div>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column">
              <QuestionBadge :id="'pendo-less-previously-paid'">
                Less Previously Paid
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <template v-if="distributions[index - 1] && distributions[index - 1].sum_of_previous !== 0">
                  {{ formatCurrency(-distributions[index - 1].sum_of_previous) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td class="first-column" />
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td>
                <div
                  class="font-bold"
                  :class="[distributions[index - 1]? 'border-t' : '']"
                >
                  <template v-if="distributions[index - 1]">
                    {{ formatCurrency(distributions[index - 1].eligible) }}
                  </template>
                </div>
              </td>
            </template>
          </tr>
          <tr v-if="distributions.some(({adjustments}) => adjustments != 0)">
            <td
              class="first-column"
              style="padding-bottom:1em;"
            >
              <QuestionBadge :id="'pendo-employer-adjustment'">
                Employer Adjustment
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td style="padding-bottom:1em;">
                <template v-if="distributions[index - 1] && distributions[index - 1].adjustments !== 0">
                  {{ distributions[index - 1].adjustments < 0 ? '-' : '+' }}{{ formatCurrency(distributions[index - 1].adjustments) }}
                </template>
              </td>
            </template>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td
              class="first-column distribution-footer"
              style="vertical-align:bottom;"
            >
              <QuestionBadge :id="'pendo-period-payment'">
                <template v-if="payoutCount === 4">
                  Quarterly Payment
                </template>
                <template v-else-if="payoutCount === 3">
                  Trimester Payment
                </template>
                <template v-else-if="payoutCount === 2">
                  Semi-annual Payment
                </template>
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td
                class="distribution-amount"
                style="vertical-align:bottom;"
              >
                <template v-if="distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].award_amount) }}
                </template>
              </td>
            </template>
          </tr>
          <tr>
            <td
              class="first-column distribution-footer"
              style="vertical-align:top;"
            >
              <QuestionBadge :id="'pendo-paid-to-date'">
                Paid to Date
              </QuestionBadge>
            </td>
            <template
              v-for="index in payoutCount"
              :key="index"
            >
              <td
                class="ytd-bonus"
                style="vertical-align:top;"
              >
                <template v-if="distributions[index - 1]">
                  {{ formatCurrency(distributions[index - 1].ytd_bonus) }}
                </template>
              </td>
            </template>
          </tr>
        </tfoot>
      </table>
    </template>
  </div>
</template>

<script>
import QuestionBadge from "@/components/pendo/QuestionBadge.vue";
import VLcurrency from "@/composables/VLcurrency";
import VLpercent from '@/composables/VLpercent';

export default {
    name: "PlanDistributions",
  components: {QuestionBadge},

    props: {
      plan: {
        type: Object,
        required: true,
      },
      client: {
        type: Object,
        required: false,
      },
      employee: {
        type: Object,
        required: true,
      },
      organization: {
        type: Object,
        required: false,
      },
      distributions: {
        type: Array,
        required: true,
      },
    },

    data() {
      return {
        selectedPeriod: null,
      }
    },

    computed: {
      distributionCount() {
        return this.distributions.length
      },
      payoutCount() {
        return this.plan?.holdback.payout_count
      },
      employeeRating() {
        return !!this.employee.performance.rating
      },
      employeeGoals() {
        return !!this.employee.performance.goals
      },
      hasOrgUnits() {
        return !!(this.organization && Object.keys(this.organization.units).length)
      },
    },

    watch: {
      selectedPeriod: {
        handler() {
          this.formatTable();
        },
      },
    },

    mounted() {
      this.selectedPeriod = this.distributionCount ? this.distributionCount : 1;
    },

    methods: {
        formatCurrency(value, decimals = 0, currency = 'USD') {
            return VLcurrency.formatCurrency(value, decimals, currency);
        },
        formatPercent(value, decimals = 0) {
            return VLpercent.formatPercent(value, decimals);
        },
        formatTable() {
          const table = document.getElementById("distributions-table");
          table.childNodes.forEach((section) => {
            section.childNodes.forEach((row) => {
              let elKey = 1; // keeps track of elements, ignores text nodes
              row.childNodes.forEach((cell, key) => {
                if (key !== 0 && elKey !== this.selectedPeriod + 1) {
                  cell.classList?.remove('active');
                } else if (cell.nodeType != 3) { // ignore text nodes
                  cell.classList?.add('active');
                }
                if (cell.nodeType != 3) { // ignore text nodes
                  elKey++;
                }
              });
            });
          });
        },
        selectPrevPeriod() {
          if (this.selectedPeriod != 1) {
            this.selectedPeriod--;
          }
        },
        selectNextPeriod() {
          if (this.selectedPeriod != this.payoutCount) {
            this.selectedPeriod++;
          }
        },
    }
}
</script>

<style scoped lang="scss">
@use "../../../scss/_variables.scss";

.distributions-header {
  display: flex;
  flex-direction: column;

  padding-top: 2.4rem;

  h2 {
    margin: 0;
  }

  @media screen and (max-width: 350px) {
    margin-left: -2.4rem;
  }
}

table {
  width: 100%;
  border-collapse: collapse;

  thead {
    border-bottom: 1px solid variables.$charcoal-tint-20;
    white-space: nowrap;
  }

  tbody {
    border-bottom:1px solid variables.$charcoal-tint-20;
  }

  tfoot>tr>td {
    border: none;
    border-radius: unset;
    text-align: right;
  }

  th,
  td {
    padding: 0.25rem 1rem;
    border: none;
    border-radius: unset;
    text-align: right;
    color: variables.$charcoal-tint-50;
    font-size: variables.$text-base;
  }

  th {
    text-align: center;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  td {
    border-right:1px solid variables.$charcoal-tint-20;
  }

  span.next-period, span.prev-period {
    display: none;
  }

  @media screen and (max-width: 350px) {
    margin-left: -2.4rem;
  }

  @media screen and (max-width: 825px) {
    th:not(.active), td:not(.active){
      display: none;
    }
    th.active, td.active{
      display: table-cell;
    }

    th.active:not(:first-child) {
      font-size: 24px;
    }

    span.next-period, span.prev-period {
      display: unset;
      text-decoration: underline;
      cursor: pointer;
      font-size: 24px;
      color: #241b89;
      &.disabled {
        color: variables.$color-button-disabled;
        cursor: default;
      }
    }

    span.next-period {
      padding-left: 8px;
    }
    span.prev-period {
      padding-right: 8px;
    }
  }
}

.distribution-symbol {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  color: variables.$charcoal-tint-50;

  span {
    width: 40%;
    color: var(--charcoal-tint-50);

    &.highlight {
      color: var(--highlight-color);
      text-align: center;
      filter: opacity(1);
    }
  }
}

.distribution-symbol.indent span {
  width: 60%;
}

.distribution-period {
  color: variables.$charcoal;
}

.forecasted-distribution {
  color: variables.$charcoal-tint-50;
  font-weight: normal;
}

.highlight {
  color: var(--highlight-color);
  filter: opacity(0.7);
}

.final-distribution {
  color: var(--highlight-color);
}

.first-column {
  color: variables.$charcoal;
  font-size: 16px;
}

.performance-level {
  font-weight: bold;
  color: var(--highlight-color);
}

.distribution-footer {
  font-weight: bold;
}

.ytd-bonus {
  color: var(--highlight-color);
  font-weight: bold;
  font-size: variables.$text-lg;
}

.distribution-amount {
  padding-top:0.75rem;
  font-weight: bold;
  font-size: variables.$text-md;
}

.font-bold {
  font-weight: bold;
}

.ml-3rem {
  margin-left: 3rem;
}

.ml-4rem {
  margin-left: 4rem;
}

.pl-4 {
  padding-left: 0.5rem;
}

.pt-0 {
  padding-top: 0;
}

.pt-4 {
  padding-top: 0.5em !important;
}

.pt-8 {
  padding-top: 1rem;
}

.border-t {
  border-top: 1px solid variables.$charcoal-tint-50;
}
</style>